/**
 * 预算成分输入组件
 * 用于项目表单中设置预算成分明细
 * 支持自动/手动预算模式
 */
import React, { useEffect, useState } from 'react';
import { 
  Card, 
  InputNumber, 
  Typography, 
  Space, 
  Progress, 
  Alert,
  Collapse,
  Row,
  Col,
  Tooltip,
  Switch,
  Tag,
  Button,
  Divider
} from 'antd';
import {
  UserOutlined,
  ToolOutlined,
  GoldOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  UnlockOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import type { BudgetBreakdown, BudgetMode } from '../../types';

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface BudgetBreakdownInputProps {
  value?: BudgetBreakdown;
  onChange?: (value: BudgetBreakdown) => void;
  totalBudget?: number;
  onTotalBudgetChange?: (value: number) => void;
  budgetMode?: BudgetMode;
  onBudgetModeChange?: (mode: BudgetMode) => void;
  budgetLocked?: boolean;
  disabled?: boolean;
  isMobile?: boolean;
  showModeSwitch?: boolean;
  showLockStatus?: boolean;
}

// 预算成分配置
const BUDGET_ITEMS = [
  {
    key: 'labor' as keyof BudgetBreakdown,
    label: '人工预算',
    icon: <UserOutlined style={{ color: '#1677ff' }} />,
    color: '#1677ff',
    description: '工人工资、加班费、福利等'
  },
  {
    key: 'equipment' as keyof BudgetBreakdown,
    label: '机械预算',
    icon: <ToolOutlined style={{ color: '#52c41a' }} />,
    color: '#52c41a',
    description: '设备租赁、燃油、维护保养等'
  },
  {
    key: 'material' as keyof BudgetBreakdown,
    label: '材料预算',
    icon: <GoldOutlined style={{ color: '#fa8c16' }} />,
    color: '#fa8c16',
    description: '原材料、辅材、消耗品等'
  },
  {
    key: 'subcontract' as keyof BudgetBreakdown,
    label: '分包预算',
    icon: <FileTextOutlined style={{ color: '#13c2c2' }} />,
    color: '#13c2c2',
    description: '分包施工、分包班组结算等'
  },
  {
    key: 'coordination' as keyof BudgetBreakdown,
    label: '协调预算',
    icon: <FileTextOutlined style={{ color: '#722ed1' }} />,
    color: '#722ed1',
    description: '管理费、协调费、杂费等'
  },
  {
    key: 'contingency' as keyof BudgetBreakdown,
    label: '应急储备',
    icon: <SafetyCertificateOutlined style={{ color: '#eb2f96' }} />,
    color: '#eb2f96',
    description: '风险应对、不可预见费用等'
  }
];

// 默认预算成分
const DEFAULT_BREAKDOWN: BudgetBreakdown = {
  labor: 0,
  equipment: 0,
  material: 0,
  subcontract: 0,
  coordination: 0,
  contingency: 0
};

const BudgetBreakdownInput: React.FC<BudgetBreakdownInputProps> = ({
  value,
  onChange,
  totalBudget = 0,
  onTotalBudgetChange,
  budgetMode = 'auto',
  onBudgetModeChange,
  budgetLocked = false,
  disabled = false,
  isMobile = false,
  showModeSwitch = true,
  showLockStatus = true
}) => {
  const [breakdown, setBreakdown] = useState<BudgetBreakdown>(DEFAULT_BREAKDOWN);
  const [isAutoMode, setIsAutoMode] = useState(budgetMode === 'auto');

  // 同步外部值
  useEffect(() => {
    if (value) {
      setBreakdown({
        labor: value.labor || 0,
        equipment: value.equipment || 0,
        material: value.material || 0,
        subcontract: value.subcontract || 0,
        coordination: value.coordination || 0,
        contingency: value.contingency || 0
      });
    }
  }, [value]);

  // 同步预算模式
  useEffect(() => {
    setIsAutoMode(budgetMode === 'auto');
  }, [budgetMode]);

  // 计算合计
  const total = breakdown.labor + breakdown.equipment + breakdown.material + breakdown.subcontract + breakdown.coordination + breakdown.contingency;
  
  // 计算与总预算的差异
  const difference = totalBudget - total;
  const isMatched = Math.abs(difference) < 0.01;

  // 处理单项变更
  const handleItemChange = (key: keyof BudgetBreakdown, newValue: number | null) => {
    const updatedBreakdown = {
      ...breakdown,
      [key]: newValue || 0
    };
    setBreakdown(updatedBreakdown);
    onChange?.(updatedBreakdown);
    
    // 自动模式下，更新总预算
    if (isAutoMode && onTotalBudgetChange) {
      const newTotal = updatedBreakdown.labor + updatedBreakdown.equipment + 
                       updatedBreakdown.material + updatedBreakdown.subcontract + updatedBreakdown.coordination + 
                       updatedBreakdown.contingency;
      onTotalBudgetChange(newTotal);
    }
  };

  // 处理模式切换
  const handleModeChange = (checked: boolean) => {
    const newMode: BudgetMode = checked ? 'auto' : 'manual';
    setIsAutoMode(checked);
    onBudgetModeChange?.(newMode);
    
    // 切换到自动模式时，同步总预算
    if (checked && onTotalBudgetChange) {
      onTotalBudgetChange(total);
    }
  };

  // 一键同步总预算
  const syncTotalBudget = () => {
    if (onTotalBudgetChange) {
      onTotalBudgetChange(total);
    }
  };

  // 计算占比
  const getPercentage = (amount: number): number => {
    if (total <= 0) return 0;
    return (amount / total) * 100;
  };

  // 格式化金额
  const formatMoney = (amount: number): string => {
    return `¥${amount.toLocaleString()}`;
  };

  // 获取预算健康状态
  const getBudgetHealthStatus = () => {
    if (total === 0) return { status: 'default', text: '未设置预算' };
    if (isMatched) return { status: 'success', text: '预算匹配' };
    if (difference > 0) return { status: 'warning', text: `少分配 ${formatMoney(difference)}` };
    return { status: 'error', text: `超分配 ${formatMoney(Math.abs(difference))}` };
  };

  const healthStatus = getBudgetHealthStatus();

  return (
    <Collapse 
      defaultActiveKey={totalBudget > 0 || total > 0 ? ['breakdown'] : []}
      style={{ marginTop: 8 }}
    >
      <Panel 
        header={
          <Space>
            <span>📊 预算成分明细</span>
            {showLockStatus && budgetLocked && (
              <Tag icon={<LockOutlined />} color="red">已锁定</Tag>
            )}
            {total > 0 && (
              <Tag color={
                healthStatus.status === 'success' ? 'green' : 
                healthStatus.status === 'warning' ? 'orange' : 
                healthStatus.status === 'error' ? 'red' : 'default'
              }>
                {healthStatus.text}
              </Tag>
            )}
          </Space>
        }
        key="breakdown"
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          {/* 预算模式切换 */}
          {showModeSwitch && (
            <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
              <Row align="middle" justify="space-between">
                <Col>
                  <Space>
                    <CalculatorOutlined style={{ color: '#1677ff' }} />
                    <Text strong>预算计算模式</Text>
                    <Tooltip title={
                      isAutoMode 
                        ? '自动模式：总预算 = 各项预算之和，修改细分预算会自动更新总预算' 
                        : '手动模式：总预算可独立设置，允许与细分预算之和不一致'
                    }>
                      <InfoCircleOutlined style={{ color: '#999', cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <Text type="secondary">手动</Text>
                    <Switch 
                      checked={isAutoMode} 
                      onChange={handleModeChange}
                      disabled={disabled || budgetLocked}
                      checkedChildren="自动"
                      unCheckedChildren="手动"
                    />
                    <Text type="secondary">自动</Text>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}

          {/* 预算成分输入 */}
          {BUDGET_ITEMS.map((item) => (
            <Card 
              key={item.key} 
              size="small" 
              style={{ backgroundColor: '#fafafa' }}
              bodyStyle={{ padding: isMobile ? 12 : 16 }}
            >
              <Row gutter={[16, 8]} align="middle">
                <Col xs={24} sm={12}>
                  <Space>
                    {item.icon}
                    <Text strong>{item.label}</Text>
                    <Tooltip title={item.description}>
                      <InfoCircleOutlined style={{ color: '#999', cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </Col>
                <Col xs={24} sm={12}>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={breakdown[item.key]}
                    onChange={(val) => handleItemChange(item.key, val)}
                    formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value!.replace(/¥\s?|(,*)/g, '')}
                    min={0}
                    step={1000}
                    disabled={disabled || budgetLocked}
                    placeholder="请输入预算金额"
                  />
                </Col>
                <Col xs={24}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Progress
                      percent={getPercentage(breakdown[item.key])}
                      strokeColor={item.color}
                      showInfo={false}
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <Text type="secondary" style={{ minWidth: 50, textAlign: 'right' }}>
                      {getPercentage(breakdown[item.key]).toFixed(1)}%
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>
          ))}

          {/* 汇总信息 */}
          <Card 
            size="small" 
            style={{ 
              backgroundColor: isMatched ? '#f6ffed' : (difference < 0 ? '#fff1f0' : '#fff7e6'),
              borderColor: isMatched ? '#b7eb8f' : (difference < 0 ? '#ffa39e' : '#ffd591')
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={12}>
                <Text type="secondary">预算成分合计</Text>
                <div>
                  <Text strong style={{ fontSize: 18 }}>
                    {formatMoney(total)}
                  </Text>
                </div>
              </Col>
              <Col xs={12}>
                <Text type="secondary">项目总预算</Text>
                <div>
                  <Text strong style={{ fontSize: 18 }}>
                    {formatMoney(totalBudget)}
                  </Text>
                  {!isAutoMode && !isMatched && !budgetLocked && (
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={syncTotalBudget}
                      style={{ padding: '0 4px' }}
                    >
                      同步
                    </Button>
                  )}
                </div>
              </Col>
              <Col xs={24}>
                {(totalBudget > 0 || total > 0) && (
                  <Alert
                    type={isMatched ? 'success' : (difference < 0 ? 'error' : 'warning')}
                    message={
                      isMatched 
                        ? '✅ 预算成分合计与总预算完全匹配' 
                        : `${difference < 0 ? '❌' : '⚠️'} 差异金额: ${formatMoney(Math.abs(difference))} (${difference > 0 ? '少分配' : '超分配'})`
                    }
                    style={{ marginTop: 8 }}
                    showIcon={false}
                  />
                )}
              </Col>
            </Row>
          </Card>

          {/* 预算分布可视化 */}
          {total > 0 && (
            <Card size="small" title="预算分布">
              <div style={{ display: 'flex', height: 24, borderRadius: 4, overflow: 'hidden' }}>
                {BUDGET_ITEMS.map((item) => {
                  const percent = getPercentage(breakdown[item.key]);
                  if (percent <= 0) return null;
                  return (
                    <Tooltip 
                      key={item.key} 
                      title={`${item.label}: ${formatMoney(breakdown[item.key])} (${percent.toFixed(1)}%)`}
                    >
                      <div 
                        style={{ 
                          width: `${percent}%`, 
                          backgroundColor: item.color,
                          transition: 'width 0.3s ease'
                        }} 
                      />
                    </Tooltip>
                  );
                })}
              </div>
              <Row gutter={[8, 4]} style={{ marginTop: 8 }}>
                {BUDGET_ITEMS.map((item) => (
                  <Col key={item.key} xs={12} sm={8} md={4}>
                    <Space size={4}>
                      <div style={{ 
                        width: 12, 
                        height: 12, 
                        backgroundColor: item.color, 
                        borderRadius: 2 
                      }} />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.label.replace('预算', '').replace('储备', '')}
                      </Text>
                    </Space>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* 提示信息 */}
          <Text type="secondary" style={{ fontSize: 12 }}>
            💡 提示：{isAutoMode 
              ? '自动模式下，修改任意预算成分会自动更新总预算' 
              : '手动模式下，总预算可独立设置，建议与成分合计保持一致'}
          </Text>
        </Space>
      </Panel>
    </Collapse>
  );
};

export default BudgetBreakdownInput;
